import { Component, ElementRef, EventEmitter, Input, OnInit, OnDestroy, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { CsvImportService } from "../../services/csv-import.service";
import { Message, SpinnerService } from "shared-components";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-csv-import",
  templateUrl: "./csv-import.component.html",
})
export class CsvImportComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  /* crops...
  Esta debe coincidir con la clave en el JSON de traducciones: csv-import.keywords !!!   <------------   */
  @Input() mainKeyword = '';

  @Output() csvStep = new EventEmitter<string>();
  @Output() openFormOrRefreshAfterClose = new EventEmitter<string>(); // se envía '' si solo hay que cerrar el modal

  @ViewChild('inputFile') inputFile!: ElementRef<HTMLInputElement>;
  @ViewChild('inputValidation', { static: false })
  inputValidation!: ElementRef<HTMLInputElement>;

  public currentStep = 'initial'; // initial, column-assignment, validation, insert-completion, summary
  public savedInputFile: any = null;
  private isFileWithUtf8 = true;
  private isEmptyTitleWithDataInColumn = false;
  private areRepeatedTitles = false;

  public templateTitles: Array<any> = []; // para los desplegables de títulos de columnas
  public columnAssignmentData: Array<any> = []; // añadiremos title, preview1, preview2
  public columnAssignmentForm!: FormGroup;
  private savedColumnForm: any = {};
  public columnTitlesAutoChecked: Array<boolean> = [];
  public totalColumnTitlesChecked = 0;

  public validationResponse: any = null;
  public validationTitles: Array<string> = [];
  public validationData: Array<any> = [];
  public editValidationData: string | null = null; // 'index,childIndex': índice en lsError, índice de título
  public validationForm!: FormGroup;
  public noErrorsAfterCorrecting = false;
  public mappedValidationData: Array<any> = [];
  public updateAndInsertInitialChecked = true; // se cambiará a false (solo insertar) en caso de desmarcar el checkbox por si se vuelve a este paso

  public insertCompletionResponse: any = {};
  public refreshParentPage = false; // tras cerrar el componente

  public showError = false;
  public mensajes = new Array<Message>();
  public toastMessages: Array<any> = [];

  public toCloseSpinner = 0;

  constructor(
    private readonly _translate: TranslateService,
    private readonly _formBuilder: FormBuilder,
    private readonly _router: Router,
    private readonly _spinnerService: SpinnerService,
    private readonly _csvImportService: CsvImportService,
  ) {}

  ngOnInit(): void {
    this.csvStep.emit(this.currentStep);
    this.templateTitles = this._csvImportService.getFilterableListTitles(
      this.mainKeyword
    );

    this.columnAssignmentForm = this._formBuilder.group({
      clientsOrSuppliers: [''], // solo tendrá un valor en la pantalla Stakeholders
    });
    this.validationForm = this._formBuilder.group({
      validationData: [''],
      updateAndInsert: [true], // false si solo hay que insertar
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Mostrar mensaje
   */
  private pushMessage(mensaje: string, classsName?: string) {
    this.showError = true;

    // Añadir clase si se pasa como parámetro (necesaria por repeatedTitleTemplate). Si no, asignar según el contenido del mensaje
    if (classsName) {
      this.mensajes.push({ class: classsName, message: mensaje });
    } else {

      if (mensaje.includes('error')) {
        this.mensajes.push({ class: 'error', message: mensaje });
      } else if (mensaje.includes('success')) {
          this.mensajes.push({ class: 'success', message: mensaje });
      } else {
        this.mensajes.push({ class: 'warning', message: mensaje });
      }
    }
  }

  public getMainKeyword() {
    return `csv-import.keywords.${this.mainKeyword}.title`;
  }

  public getSecondaryKeyword() {
    return `csv-import.keywords.${this.mainKeyword}.new`;
  }

  public closeModalAndOpenForm(value: string) {
    this.openFormOrRefreshAfterClose.emit(value);
  }

  private getDocOrTemplateFilters() {
    let filters = {};

    // TO-DO: añadir filtros específicos a medida que el componente se implemente en otras pantallas   !!!   <------------
    // if (this.mainKeyword.includes('...')) {
    //   filters = {
    //   };
    // }

    return filters;
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
        this._spinnerService.hideSpinner();
    }
  }

  /**
   * Abrir el documento en una nueva pestaña
   */
  public getSpecificationDoc() {
    const filters = this.getDocOrTemplateFilters();
    const screen = this.getEndpointFragment();

    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._csvImportService.getCsvSpecificationDoc(screen, filters).toPromise().finally(() => {
        this.checkCloseSpinner();
    }).then((resp) => {
        localStorage.setItem('base64', resp.base64Archivo);
        const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
        window.open(url, '_blank');
    }, (error) => {
        this.pushMessage('global.error.noResponse');
    });
  }

  public downloadTemplate() {
    const endpointFragment = this.getEndpointFragment();
    const filters = this.getDocOrTemplateFilters();
    this._spinnerService.showSpinner();

    this._csvImportService.getCsvTemplate(endpointFragment, filters).toPromise().finally(() => {
        this._spinnerService.hideSpinner();
    }).then((resp) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = 'data:text/csv;base64,' + resp.base64Archivo;
        downloadLink.download = resp.nombreArchivo;
        downloadLink.click();

        this.pushMessage(
          'csv-import.success.template'
        );
    }, (error) => {
        this.pushMessage('global.error.noResponse');
    });
  }

  public openInputFile() {
    this.inputFile.nativeElement.click();
  }

  public saveInputFile(event: Event) {
    this.savedInputFile = null;
    const input = event.target as HTMLInputElement;

    if (input?.files && input.files?.length > 0) {
      this.savedInputFile = input.files[0];
    }
  }

  /**
   * Marcar/desmarcar el checkbox de la fila dependiendo del valor de los desplegables
   */
  private subscribeToFilterableLists() {
    this.columnAssignmentData.forEach((data, index) => {
      const control = data.title.toLowerCase().replace(/ /g, '');
      const controlList = control + 'Template';
      this.columnAssignmentForm
        .get(controlList)
        ?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
          if (value) {
            this.columnTitlesAutoChecked.splice(index, 1, true);
          } else {
            this.columnTitlesAutoChecked.splice(index, 1, false);
          }
        });
    });
  }

  private getTemplateTitleMatch(control: any) {
    const templateTitle = this.templateTitles.find(title => {
      // Comprobaciones extra en español. TO-DO: añadir más si es necesario !!!
      if (control === 'pais') {
        return title.des === 'Pais';
      } else if (
        control.includes('variedad')
      ) {
        return title.des.includes('Denominacion');
      } else if (control.includes('condado') || control.includes('estado')) {
        return title.des.includes('Provincia');
      } else if (control.includes('año')) {
        return title.des.includes('Campaña');
      } else if (
        // Comprobaciones extra en inglés. TO-DO: añadir más si es necesario !!!
        control === 'country'
      ) {
        return title.des === 'Country';
      } else if (control.includes('variety')) {
        return title.des.includes('Denomination');
      } else if (control.includes('county') || control.includes('state')) {
        return title.des.includes('Province');
      } else if (control.includes('year')) {
        return title.des.includes('Season');
      } else {
        // Resto de casos: devolver el título de plantilla que incluya el del archivo subido
        return control !== '' && title.des.toLowerCase().includes(control);
      }
    });
    
    return templateTitle;
  }

  /**
   * Por cada columna del CSV, crear los controles necesarios (para el checkbox, input y filterable list) en Asignación de columnas
   */
  private createControlsForColumnTitles() {
    this.columnAssignmentData.forEach(data => {
      const control = data.title.toLowerCase().replace(/ /g, '');
      const templateTitleMatch = this.getTemplateTitleMatch(control);

      this.columnAssignmentForm.addControl(
        control,
        new FormControl(data.title)
      );
      this.columnAssignmentForm.addControl(
        `${control}Check`,
        new FormControl(templateTitleMatch ? true : false)
      );
      this.columnAssignmentForm.addControl(
        `${control}Template`,
        new FormControl('')
      );

      // Si el título de la columna del CSV importado coincide con alguno de la plantilla, actualizamos el desplegable y marcamos el checkbox
      setTimeout(() => {
        this.columnAssignmentForm
          .get(`${control}Template`)
          ?.patchValue(templateTitleMatch ?? '');

        this.subscribeToFilterableLists();
      }, 100);
      this.columnTitlesAutoChecked.push(templateTitleMatch ? true : false);
    });
  }

  public getArrowDisabled(title: string) {
    const control = title.toLowerCase().replace(/ /g, '');
    return !this.columnAssignmentForm.get(`${control}Check`)?.value;
  }

  private getInputFileTitlesAndPreview() {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      let decoder: any;
      let text: any;

      // Asegurar que el CSV se importa en codificación UTF-8 (si no, en la tabla con la respuesta de back no se leen correctamente caracteres especiales como la Ñ)
      try {
        this.isFileWithUtf8 = true;
        decoder = new TextDecoder('utf-8', { fatal: true });
        text = decoder.decode(arrayBuffer);
      } catch {
        this.isFileWithUtf8 = false;
        decoder = new TextDecoder('iso-8859-1');
        text = decoder.decode(arrayBuffer);

        // Nota a futuro: eliminar mensaje de error si se admiten otras codificaciones
        this.pushMessage(
          'csv-import.errors.encoding'
        );
      }

      // Nota a futuro: eliminar condición si se admiten otras codificaciones
      if (!this.isFileWithUtf8) {
        return; // cortamos el código aquí
      }

      const lines = text
        .split(/\r\n|\n/)
        .filter((line: any) => line.trim() !== ''); // Eliminar líneas vacías

      if (lines.length > 0) {
        if (!lines[0].includes(';')) {
          this.pushMessage(
            'csv-import.errors.fileFormat'
          );
          return;
        }

        const titles = lines[0].split(';');
        const preview1 = lines[1]?.split(';');
        const preview2 = lines[2]?.split(';');
        const finalTitles: Array<any> = [];
        const finalPreview1: Array<any> = [];
        const finalPreview2: Array<any> = [];

        titles.forEach((title: string, colIndex: number) => {
          if (title.trim() === '') {
            lines.forEach((line: any, index: number) => {
              // la primera posición corresponde a los títulos
              if (index === 0) {
                return;
              }
              const lineValues = line.split(';');
              const value = lineValues[colIndex]?.trim();

              // Título vacío, pero con datos en la columna (se mostrará mensaje de error)
              if (value !== '') {
                this.isEmptyTitleWithDataInColumn = true;
                return;
              }
            });
            // Solo incluimos los títulos con valor
          } else {
            finalTitles.push(title);
            finalPreview1.push(preview1[colIndex]);
            finalPreview2.push(preview2 ? preview2[colIndex] : '');
          }
        });

        // Para mostrar mensaje de error en caso de que se hayan subido títulos repetidos
        const noRepeatedTitles = new Set(finalTitles);
        if (noRepeatedTitles.size !== finalTitles.length) {
          this.areRepeatedTitles = true;
          return;
        }

        this.columnAssignmentData = finalTitles.map(
          (title: any, index: number) => ({
            title: title,
            preview1: finalPreview1?.[index],
            preview2: finalPreview2?.[index],
          })
        );

        this.createControlsForColumnTitles();
      }
    };

    reader.readAsArrayBuffer(this.savedInputFile);
  }

  public changeToColumnTitlesView() {
    if (this.savedInputFile) {
      this.getInputFileTitlesAndPreview();

      setTimeout(() => {
        if (this.isEmptyTitleWithDataInColumn) {
          this.pushMessage(
            'csv-import.errors.emptyTitle'
          );
          return;
        }

        if (this.areRepeatedTitles) {
          this.pushMessage(
            'csv-import.errors.repeatedTitles'
          );
          return;
        }

        if (this.isFileWithUtf8) {
          this.currentStep = 'column-assignment';
          this.csvStep.emit(this.currentStep);
          this.getCheckedTitles();
        }
      }, 100);
    } else {
      this.pushMessage(
        'csv-import.errors.fileSelection'
      );
    }
  }

  public goBack(previousSection: string) {
    this.currentStep = previousSection;
    this.csvStep.emit(this.currentStep);

    switch (this.currentStep) {
      case 'columnAssignment':
        setTimeout(() => {
          this.columnAssignmentForm.patchValue(this.savedColumnForm);
        }, 100);
        break;
    }
  }

  /**
   * Relacionar los controles en el formulario del paso 3 (asignación de columnas)
   */
  public updateCheckbox(title: string, value: any) {
    const control = title.toLowerCase().replace(/ /g, '');
    const controlCheck = control + 'Check';
    this.columnAssignmentForm.get(controlCheck)?.patchValue(value);

    // Si se desmarca un checkbox, limpiamos su desplegable
    if (!value) {
      const controlTemplate = control + 'Template';
      this.columnAssignmentForm.get(controlTemplate)?.patchValue(null);
    }

    this.getCheckedTitles();
  }

  public getInputControlName(title: string) {
    return title.toLowerCase().replace(/ /g, '');
  }

  public getFilterableListControl(title: string) {
    const control = title.toLowerCase().replace(/ /g, '');
    const controlList = control + 'Template';
    return this.columnAssignmentForm.get(controlList);
  }

  public getCheckedTitles() {
    let totalChecked = 0;
    this.columnAssignmentData.forEach(data => {
      const control = data.title.toLowerCase().replace(/ /g, '');

      this.columnAssignmentForm.get(`${control}Check`)?.value
        ? (totalChecked = totalChecked + 1)
        : null;

      setTimeout(() => {
        this.totalColumnTitlesChecked = totalChecked;
      }, 100);
    });
  }

  /**
   * Obtener JSON únicamente de las filas marcadas cuyos desplegables tengan un valor
   */
  private getKeyValuePairsOfColumnTitles() {
    let keyValuePairs = {};

    this.columnAssignmentData.forEach((data: any) => {
      const control = data.title.toLowerCase().replace(/ /g, '');
      const controlList = control + 'Template';
      const controlCheck = control + 'Check';

      if (this.columnAssignmentForm.get(controlCheck)?.value) {
        keyValuePairs = {
          ...keyValuePairs,
          [data.title]:
            this.columnAssignmentForm.get(controlList)?.value?.des || '',
        };
      }
    });

    return keyValuePairs;
  }

  /**
   * Sumar al objeto anterior de pares de títulos asignados los NO marcados
   * para evitar que se ignore la decisión del usuario de NO importar x columnas
   */
  private addNoCheckedTitlesToKeyValuePairs() {
    let keyValuePairs = this.getKeyValuePairsOfColumnTitles();

    this.columnAssignmentData.forEach((data: any) => {
      const control = data.title.toLowerCase().replace(/ /g, '');
      const controlCheck = control + 'Check';

      if (!this.columnAssignmentForm.get(controlCheck)?.value) {
        keyValuePairs = {
          ...keyValuePairs,
          [data.title]: 'NoMappear',
        };
      }
    });

    return keyValuePairs;
  }

  private getEndpointFragment() {
    let endpointFragment = '';

    // TO-DO: añadir condiciones a medida que el componente se implemente en otras pantallas   !!!   <------------
    switch (this.mainKeyword) {
      case 'crops':
        endpointFragment = '/Finca';
        break;
      case 'producers':
        endpointFragment = '/ApplicationUser';
        break;
    }

    return endpointFragment;
  }

  /**
   * Convertimos el objeto con los valores para la tabla a un array para iterarlo automáticamente en todas las pantallas
   */
  private mapValidationResponseForTable(errorList: any) {
    this.validationTitles = []; // limpiamos en caso de volver a la asignación de columnas y continuar

    this.validationData = errorList.map((element: any, index: number) => {
      const allValues: any[] = [];

      Object.keys(element.item).forEach(key => {
        const value = element.item[key];

        if (index === 0) {
          // Obtenemos los títulos
          let modifiedKey = key;

          // La clave en español "campanya" debe mostrarse como el título "campaña"
          if (key.toLowerCase() === 'campanya') {
            modifiedKey = 'campaña';
          }

          this.validationTitles.push(modifiedKey);
        }

        // Guardamos cada valor de la fila y su error, en caso de que lo tenga
        let errorOnKey = key.toLowerCase() === element.clave.toLowerCase();

        // Asegurar que se muestre el icono de error para "campaña"
        if (key.toLowerCase() === 'campanya' && element.clave.toLowerCase() === 'campaña') {
          errorOnKey = true;
        }

        allValues.push([value, errorOnKey ? element.error : 'correct']);
      });

      return { values: allValues };
    });
  }

  private getUploadFormData() {
    // La llamada necesita que el body sea un form-data
    const formData = new FormData();
    formData.append('archivo', this.savedInputFile);
    formData.append(
      'configString',
      JSON.stringify(this.addNoCheckedTitlesToKeyValuePairs())
    );

    return formData;
  }

  private removeCropsOptionalTitles(titles: any) {
    const lang = this._translate.getDefaultLang();
    if (lang.toLowerCase().includes('es')) {
      titles.delete('Productor');
      titles.delete('NumeroIdentificacionFiscalProductor');
      titles.delete('Licencias');
    } else {
      titles.delete('Producer');
      titles.delete('ProducerTaxIdentificationNumber');
      titles.delete('Licenses');
    }

    return titles;
  }

  private removeProducersOptionalTitles(titles: any) {
    const lang = this._translate.getDefaultLang();
    if (lang.toLowerCase().includes('es')) {
      titles.delete('Direccion');
      titles.delete('PersonaContacto');
      titles.delete('Telefono');
    } else {
      titles.delete('Address');
      titles.delete('ContactPerson');
      titles.delete('PhoneNumber');
    }

    return titles;
  }

  /* Mostramos error si no se han seleccionado todos los títulos requeridos (en Cultivos hay 5 campos opcionales) */
  private checkRequiredTemplateTitles(noDuplicateTitles: any): boolean {
    let requiredNotSelected = false;
    // TO-DO: Añadir variables y condiciones si hay campos opcionales  !!!   <------------
    const cropsNoDuplicateNoOptional = noDuplicateTitles;
    const producersNoDuplicateNoOptional = noDuplicateTitles;
    this.removeCropsOptionalTitles(cropsNoDuplicateNoOptional);
    this.removeProducersOptionalTitles(producersNoDuplicateNoOptional);

    // Para casos donde todos los títulos sean obligatorios
    if (
      this.mainKeyword !== 'crops' &&
      this.mainKeyword !== 'producers' &&
      this.templateTitles.length !== noDuplicateTitles.size
    ) {
      this.pushMessage(
        'csv-import.errors.required'
      );

      requiredNotSelected = true;
    } else if (
      this.mainKeyword === 'crops' &&
      this.templateTitles.length - 3 !== cropsNoDuplicateNoOptional.size
    ) {
      // TO-DO: añadir mensaje de error
      this.pushMessage(
        'csv-import.errors.requiredButProducer'
      );

      requiredNotSelected = true;
    } else if (
      this.mainKeyword === 'producers' &&
      this.templateTitles.length - 3 !== producersNoDuplicateNoOptional.size
    ) {
      this.pushMessage(
        'csv-import.errors.requiredButPersonalInformation'
      );

      requiredNotSelected = true;
    }

    return requiredNotSelected;
  }

  /**
   * Comprobar desplegables por si el usuario ha asignado títulos duplicados o vacíos a importar
   */
  private checkRepeatedOrEmptyTemplateTitles() {
    let requiredTitle = false;
    let duplicateTitle = false;
    const checkedTemplateTitles: any = [];
    Object.entries(this.getKeyValuePairsOfColumnTitles()).forEach(
      ([key, value]) => {
        checkedTemplateTitles.push(value);
      }
    );

    const emptyTitle = checkedTemplateTitles.some(
      (title: any) => title.trim() === ''
    );
    const filteredTemplateTitles = checkedTemplateTitles.filter(
      (title: any) => title.trim() !== ''
    ); // con valor
    const noDuplicateTemplateTitles = new Set(filteredTemplateTitles);

    // Mostramos error si hay algún título asignado vacío
    if (emptyTitle) {
      this.pushMessage(
        'csv-import.errors.emptyTitleTemplate'
      );
    }

    // Mostramos error por cada título duplicado
    if (
      filteredTemplateTitles.length > 0 &&
      noDuplicateTemplateTitles.size !== filteredTemplateTitles.length
    ) {
      const repeated = [
        ...new Set(
          filteredTemplateTitles.filter(
            (title: any, index: number, array: any) =>
              array.indexOf(title) !== index
          )
        ),
      ];
      repeated.forEach(title => {
        const message = `${this._translate.instant('csv-import.errors.repeatedTitleTemplate')} ${title}`
        this.pushMessage(
            message, 'error'
        );
      });

      duplicateTitle = true;
    }

    // Títulos requeridos
    requiredTitle = this.checkRequiredTemplateTitles(
      noDuplicateTemplateTitles
    );

    return duplicateTitle || emptyTitle || requiredTitle;
  }

  /**
   * Tras hacer click en Continuar, en Asignación de columnas
   */
  public uploadAndValidateCsv() {
    const duplicateEmptyOrRequiredTemplateTitles =
      this.checkRepeatedOrEmptyTemplateTitles();

    if (!duplicateEmptyOrRequiredTemplateTitles) {
      this.savedColumnForm = this.columnAssignmentForm.value; // guardamos el form en caso de volver atrás más adelante
      const endpointFragment = this.getEndpointFragment();
      const formData = this.getUploadFormData();
      this._spinnerService.showSpinner();

      this._csvImportService.importCsvTemporarily(endpointFragment, formData).toPromise().finally(() => {
          this._spinnerService.hideSpinner();
      }).then((resp) => {
        // Mostramos la siguiente tabla de corrección de errores si es necesaria. Si no, pasamos al modal de completar la importación
        if (resp.lsError?.length === 0) {
          this.noErrorsAfterCorrecting = true;
        }

        this.currentStep = 'validation';
        this.csvStep.emit(this.currentStep);
        this.validationResponse = resp;
        this.mapValidationResponseForTable(resp.lsError);
      }, (error) => {
          this.pushMessage('csv-import.errors.upload');
      });
    }
  }

  private subscribeToValidationInput() {
    this.validationForm.get('validationData')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      setTimeout(() => {
        this.inputValidation.nativeElement.size =
          value.length > 1 ? value.length - 1 : 1;
      }, 110);
    });
  }

  /**
   * Guardamos la actualización previa del input, en caso de haberla
   */
  private updateValidationData(index: number, childIndex: number) {
    // Si se hace click en otra celda
    if (
      this.editValidationData &&
      this.editValidationData !== `${index},${childIndex}`
    ) {
      const newValuePreviousInput = this.validationForm
        .get('validationData')
        ?.value.trim();
      const previousParentI = this.editValidationData.split(
        ','
      )[0] as unknown as number;
      const previousChildId = this.editValidationData.split(
        ','
      )[1] as unknown as number;
      this.validationData[previousParentI].values[previousChildId].splice(
        0,
        1,
        newValuePreviousInput
      );
      // Si se hace click en la misma celda
    } else if (this.editValidationData === `${index},${childIndex}`) {
      const currentValueInput = this.validationForm
        .get('validationData')
        ?.value.trim();
      this.validationData[index].values[childIndex].splice(
        0,
        1,
        currentValueInput
      );
    }
  }

  /**
   * Al hacer click en alguna celda con error de la tabla, se "muestra" el input
   */
  public showValidationInput(index: number, childIndex: number) {
    this.updateValidationData(index, childIndex);
    this.editValidationData = `${index},${childIndex}`;
    this.subscribeToValidationInput();

    const value = this.validationData[index].values[childIndex][0];
    this.validationForm.get('validationData')?.patchValue(value);
    setTimeout(() => {
      this.inputValidation.nativeElement.focus();
    }, 100);
  }

  /**
   * Convertir el array de errores del tbody de la tabla a array de JSON para Guardar cambios
   */
  private mapValidationData() {
    this.mappedValidationData = this.validationData.map(el => {
      const object: any = {};
      el.values.forEach((childEl: any, i: number) => {
        const key = this.validationTitles[i];
        const keyTransformed = key[0].toUpperCase() + key.slice(1);
        object[keyTransformed] = childEl[0];
      });

      return object;
    });

    return this.mappedValidationData;
  }

  /**
   * Si todas las celdas de la columna contienen errores, se muestra solo un icono junto al título de la misma
   */
  public showTableHeaderIcon(index: number) {
    let errorsInCol = 0;

    this.validationData.forEach(el => {
      el.values.forEach((childEl: any, childI: number) => {
        if (childI === index && childEl[1].includes('formato')) {
          errorsInCol += 1;
        }
      });
    });

    return errorsInCol === this.validationData.length;
  }

  /**
   * En caso de editar un input y darle a continuación al botón "Guardar cambios"
   * (Asegurar que este valor sobrescriba el anterior en esa posición)
   */
  private saveLastInputValue() {
    if (this.editValidationData) {
      const lastValueToSave = this.validationForm.get('validationData')?.value;
      const parentI = this.editValidationData?.split(
        ','
      )[0] as unknown as number;
      const childI = this.editValidationData?.split(
        ','
      )[1] as unknown as number;
      this.validationData[parentI].values[childI].splice(0, 1, lastValueToSave);
    }
  }

  private getCorrectFilters() {
    let finalFilters = {};
    const filters = {
      lsRegIns: this.validationResponse.lsRegIns,
      lsRegUpd: this.validationResponse.lsRegUpd,
      lsRegNoEditables: this.validationResponse.lsRegNoEditables,
      lsCorreccionesJson: JSON.stringify(this.mapValidationData()),
      nombreArchivoErrores: this.validationResponse.nombreArchivoErrores,
    };

    finalFilters = {
      ...filters,
    };

    // // Para filtros extra
    // if (
    //   this.mainKeyword === '...'
    // ) {
    //   finalFilters = {
    //     ...filters,
    //     extra: '...',
    //   };
    // }

    return finalFilters;
  }

  public correctValidationData() {
    this.saveLastInputValue();
    const endpointFragment = this.getEndpointFragment();
    const filters = this.getCorrectFilters();
    this._spinnerService.showSpinner();

    this._csvImportService.correctCsv(endpointFragment, filters).toPromise().finally(() => {
          this._spinnerService.hideSpinner();
      }).then((resp) => {
        if (resp.lsError?.length === 0) {
          this.noErrorsAfterCorrecting = true; // Mostrar texto "No se han encontrado errores"
        } else if (resp.lsError?.length > 0) {
          this.noErrorsAfterCorrecting = false;
          this.editValidationData = null; // reseteamos el dato editado
          this.mapValidationResponseForTable(resp.lsError);
        }

        this.validationResponse = resp;
        this.pushMessage(
          'csv-import.info.validation'
        );
      }, (error) => {
          this.pushMessage('csv-import.errors.save');
      });
  }

  public changeToInsertCompletion() {
    this.updateAndInsertInitialChecked =
      this.validationForm.get('updateAndInsert')?.value; // en caso de querer volver atrás luego
    this.currentStep = 'insert-completion';
    this.csvStep.emit(this.currentStep);

    if (
      this.validationResponse.numRegNoEditables > 0 &&
      (this.mainKeyword.includes('shipments') ||
        this.mainKeyword.includes('lots'))
    ) {
      this.pushMessage(
        'csv-import.info.reshipped',
      );
    }
  }

  /**
   * Para el paso de Completar la importación
   */
  public getTotalRecords(action: string) {
    const singularText =
      this._translate.instant('csv-import.record') +
      ' ' +
      this._translate.instant('csv-import.insertCompletion.p1Singular');
    const pluralText =
      this._translate.instant('csv-import.records') +
      ' ' +
      this._translate.instant('csv-import.insertCompletion.p1Plural');

    if (action === 'insert-only') {
      if (this.validationResponse?.numRegIns === 1) {
        return `${this.validationResponse.numRegIns} ${singularText}`;
      } else {
        return this.validationResponse.numRegIns ? `${this.validationResponse.numRegIns} ${pluralText}` : `0 ${pluralText}`;
      }
      // Si también hay que actualizar
    } else {
      const insertAndUpdateCount =
        this.validationResponse?.numRegOk -
        this.validationResponse?.numRegNoEditables;
      if (insertAndUpdateCount === 1) {
        return `${insertAndUpdateCount} ${singularText}`;
      } else {
        return insertAndUpdateCount ? `${insertAndUpdateCount} ${pluralText}` : `0 ${pluralText}`;
      }
    }
  }

  private getCompleteImportFilters() {
    let finalFilters = {};
    const filters = {
      regInsUpd: this.validationResponse, // pasamos la respuesta completa
      // TO-DO: añadir ternario para pantallas donde no se pueda actualizar   !!!   <------------
      actualizarRegistrosExistentes: this.updateAndInsertInitialChecked,
    };

    finalFilters = {
      ...filters,
    };

    // // Para filtros extra
    // if (
    //   this.mainKeyword === '...'
    // ) {
    //   finalFilters = {
    //     ...filters,
    //     extra: '...',
    //   };
    // }

    return finalFilters;
  }

  /**
   * Finalmente, insertar el CSV en BD (yay!)
   */
  public completeImport() {
    const endpointFragment = this.getEndpointFragment();
    const filters = this.getCompleteImportFilters();
    this._spinnerService.showSpinner();

    this._csvImportService.insertCsv(endpointFragment, filters).toPromise().finally(() => {
          this._spinnerService.hideSpinner();
      }).then((resp) => {
        this.insertCompletionResponse = resp;
        this.currentStep = 'summary';
        this.csvStep.emit(this.currentStep);

        // Para refrescar la página al cerrar solo cuando sea necesario
        if (resp.numRegOk && resp.numRegOk > 0) {
          this.refreshParentPage = true;
        }
      }, (error) => {
          this.pushMessage('csv-import.errors.insert');
      });
  }
}
